import { isSupabaseConfigured, supabase } from '../lib/supabase';

const ACCOUNT_RETURN_KEY = 'beloved_account_return';
const ACCOUNT_SIGN_IN_ENABLED =
  (process.env.REACT_APP_ACCOUNT_SIGN_IN_ENABLED || '').trim().toLowerCase() === 'true';
const SAFE_ACCOUNT_RETURNS = new Set(['settings', 'plus', 'protect']);

function safeAccountReturn(value) {
  return SAFE_ACCOUNT_RETURNS.has(value) ? value : 'settings';
}

export function canUseAccounts() {
  return ACCOUNT_SIGN_IN_ENABLED && isSupabaseConfigured();
}

export function normalizeAccountEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function accountRedirectUrl(returnTo) {
  const safeReturn = safeAccountReturn(returnTo);
  try {
    const url = new URL(window.location.href);
    url.hash = '';
    url.search = '';
    url.searchParams.set('account', 'confirmed');
    url.searchParams.set('return', safeReturn);
    return url.toString();
  } catch (error) {
    return `${window.location.origin}${window.location.pathname}?account=confirmed&return=${safeReturn}`;
  }
}

export async function getCurrentAccount() {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) return null;
    return data && data.user ? data.user : null;
  } catch (error) {
    return null;
  }
}

export function watchAccount(callback) {
  if (!supabase) return () => {};
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session && session.user ? session.user : null, _event);
  });
  return () => {
    if (data && data.subscription) data.subscription.unsubscribe();
  };
}

export async function emailSignInCode(email, returnTo = 'settings') {
  if (!supabase) return { status: 'unavailable' };
  const normalizedEmail = normalizeAccountEmail(email);
  if (!/^\S+@\S+\.\S+$/.test(normalizedEmail)) return { status: 'invalid' };

  const safeReturn = safeAccountReturn(returnTo);
  try {
    window.localStorage.setItem(ACCOUNT_RETURN_KEY, safeReturn);
  } catch (error) {
    // The sign-in link still works when browser storage is unavailable.
  }

  try {
    const { error } = await supabase.auth.signInWithOtp({
      email: normalizedEmail,
      options: {
        // Supabase sends the six-digit OTP when the email template contains
        // `{{ .Token }}`. Keeping a route-derived redirect also makes an older
        // magic-link template a safe fallback without losing the /app route.
        emailRedirectTo: accountRedirectUrl(safeReturn),
        shouldCreateUser: true,
      },
    });
    if (error) return { status: 'error' };
    return { status: 'sent', email: normalizedEmail };
  } catch (error) {
    return { status: 'error' };
  }
}

// Backwards-compatible name for any older call sites. New UI uses a same-tab
// code so a purchase is never stranded in an email app's separate browser.
export const emailSignInLink = emailSignInCode;

export async function verifyEmailSignInCode(email, code) {
  if (!supabase) return { status: 'unavailable' };
  const normalizedEmail = normalizeAccountEmail(email);
  const normalizedCode = String(code || '').replace(/\s+/g, '');
  if (!/^\S+@\S+\.\S+$/.test(normalizedEmail) || !/^\d{6}$/.test(normalizedCode)) {
    return { status: 'invalid' };
  }

  try {
    const { data, error } = await supabase.auth.verifyOtp({
      email: normalizedEmail,
      token: normalizedCode,
      type: 'email',
    });
    if (error) {
      return error.status && Number(error.status) >= 500
        ? { status: 'error' }
        : { status: 'invalid' };
    }
    const user =
      (data && data.user) ||
      (data && data.session && data.session.user) ||
      null;
    return user ? { status: 'success', user } : { status: 'error' };
  } catch (error) {
    return { status: 'error' };
  }
}

export function consumeAccountReturn() {
  try {
    const storedReturn = window.localStorage.getItem(ACCOUNT_RETURN_KEY) || '';
    window.localStorage.removeItem(ACCOUNT_RETURN_KEY);
    const url = new URL(window.location.href);
    const isConfirmedReturn = url.searchParams.get('account') === 'confirmed';
    const rawUrlReturn = url.searchParams.get('return');
    const urlReturn = isConfirmedReturn && rawUrlReturn
      ? safeAccountReturn(rawUrlReturn)
      : '';
    if (isConfirmedReturn) {
      url.searchParams.delete('account');
      url.searchParams.delete('return');
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`
      );
    }
    return urlReturn || (SAFE_ACCOUNT_RETURNS.has(storedReturn) ? storedReturn : '');
  } catch (error) {
    return '';
  }
}

export async function signOutAccount() {
  if (!supabase) return { status: 'unavailable' };
  try {
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    return { status: error ? 'error' : 'success' };
  } catch (error) {
    return { status: 'error' };
  }
}

export function requestAccountDeletion(email) {
  const subject = encodeURIComponent('Delete my Beloved account');
  const body = encodeURIComponent(
    `Please delete my Beloved account${email ? ` (${email})` : ''}.\n\nI understand this will remove my account sign-in. My device-only prayers and saved content can be removed separately from my device.`
  );
  window.location.href = `mailto:hello@yourlaunchkit.co.uk?subject=${subject}&body=${body}`;
}
